import { Component, EventEmitter, Input, Output, signal, ChangeDetectionStrategy, input } from "@angular/core";
import { CommonModule } from "@angular/common";
import { TelemetryService } from "../../services/telemetry.service";
import { TaskCompetingUtil } from "./task.competing.util";

/**
 * Reusable component for displaying competing tasks confirmation dialog.
 * Used across task components to provide consistent UX when approving tasks with competing requests.
 */
@Component({
    selector: "s25-task-competing-confirmation",
    template: `
        @if (show()) {
            <div [ngClass]="{ 'modal-overlay': !inline }" (click)="!inline ? onCancel() : null">
                <div
                    [ngClass]="{ 'modal-content': !inline, 'competing-confirmation': true, inline: inline }"
                    (click)="$event.stopPropagation()"
                >
                    @if (!inline) {
                        <h3>Competing Requests</h3>
                    }
                    <div class="competing-message cn-alert--warning">{{ message() }}</div>
                    <div class="competing-actions">
                        <button
                            [ngClass]="{
                                'aw-button': true,
                                'aw-button--primary': true,
                                'c-margin-right--single': inline,
                            }"
                            (click)="onAction('deny')"
                            (keydown.enter)="onAction('deny')"
                            [disabled]="disabled()"
                        >
                            Assign & Deny Others
                        </button>
                        <button
                            [ngClass]="{
                                'aw-button': true,
                                'aw-button--primary': true,
                                'c-margin-right--single': inline,
                            }"
                            (click)="onAction('waitlist')"
                            (keydown.enter)="onAction('waitlist')"
                            [disabled]="disabled()"
                        >
                            Assign, then Deny/Add Others to Waitlist
                        </button>
                        <button
                            [ngClass]="{
                                'aw-button': true,
                                'aw-button--secondary': true,
                                'c-margin-right--single': inline,
                            }"
                            (click)="onAction('skip')"
                            (keydown.enter)="onAction('skip')"
                            [disabled]="disabled()"
                        >
                            Assign Only
                        </button>
                        <button
                            class="aw-button aw-button--outline"
                            (click)="onCancel()"
                            (keydown.enter)="onCancel()"
                            [disabled]="disabled()"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        }
    `,
    styles: `
        :host {
            display: block;
        }

        /* Modal overlay styles */
        :host .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
        }

        :host .modal-content {
            background: white;
            padding: 24px;
            border-radius: 8px;
            min-width: 500px;
            max-width: 600px;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
        }

        :host .modal-content h3 {
            margin: 0 0 16px 0;
            color: #333;
            font-size: 1.25rem;
        }

        /* Dark mode support for modal */
        :host-context(#s25.nm-party--on) .modal-content {
            background: #2a2b32;
            color: #e5e5e5;
        }

        :host-context(#s25.nm-party--on) .modal-content h3 {
            color: #e5e5e5;
        }

        /* Inline confirmation styles */
        :host .competing-confirmation {
            margin-top: 10px;
            padding: 15px;
        }

        :host .competing-confirmation.inline {
            margin-top: 0;
        }

        :host .competing-message {
            margin-bottom: 12px;
            font-weight: 600;
        }

        :host .competing-actions {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
            align-items: center;
        }
    `,
    standalone: true,
    imports: [CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class S25TaskCompetingConfirmationComponent {
    show = input.required<boolean>(); // Whether to show the confirmation dialog
    message = input.required<string>(); // The message to display to the user
    count = input<number>(); // Number of competing tasks (for display)
    disabled = input<boolean>(); // Whether the buttons should be disabled
    @Input() inline: boolean = false;
    @Output() confirm = new EventEmitter<TaskCompetingUtil.CompetingAction>();
    @Output() cancel = new EventEmitter<void>();

    onAction(action: TaskCompetingUtil.CompetingAction) {
        TelemetryService.sendWithSub("Task", "Competing", "Confirm - " + action);
        this.confirm.emit(action);
    }

    onCancel() {
        TelemetryService.sendWithSub("Task", "Competing", "Confirm - " + "cancel");
        this.cancel.emit();
    }
}
